import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ─── Canvas block layout ──────────────────────────────────────
const CANVAS_BLOCKS = [
  { id:'key_partnerships',      label:'Key Partnerships',      short:'KP', color:'#7c6fcd' },
  { id:'key_activities',        label:'Key Activities',        short:'KA', color:'#4a7c3f' },
  { id:'key_resources',         label:'Key Resources',         short:'KR', color:'#2a9d8f' },
  { id:'value_propositions',    label:'Value Propositions',    short:'VP', color:'#4a5481' },
  { id:'customer_relationships',label:'Customer Relationships',short:'CR', color:'#e8522a' },
  { id:'channels',              label:'Channels',              short:'CH', color:'#f4a261' },
  { id:'customer_segments',     label:'Customer Segments',     short:'CS', color:'#e76f51' },
  { id:'cost_structure',        label:'Cost Structure',        short:'C$', color:'#6b6b6b' },
  { id:'revenue_streams',       label:'Revenue Streams',       short:'R$', color:'#1a936f' },
];

const CANVAS_GRID = [
  { blockId:'key_partnerships',      gridColumn:'1',          gridRow:'1 / span 2' },
  { blockId:'key_activities',        gridColumn:'2',          gridRow:'1' },
  { blockId:'key_resources',         gridColumn:'2',          gridRow:'2' },
  { blockId:'value_propositions',    gridColumn:'3',          gridRow:'1 / span 2' },
  { blockId:'customer_relationships',gridColumn:'4',          gridRow:'1' },
  { blockId:'channels',              gridColumn:'4',          gridRow:'2' },
  { blockId:'customer_segments',     gridColumn:'5',          gridRow:'1 / span 2' },
  { blockId:'cost_structure',        gridColumn:'1 / span 2', gridRow:'3' },
  { blockId:'revenue_streams',       gridColumn:'4 / span 2', gridRow:'3' },
];

const JOURNEY_STAGE_DEFAULTS = {
  customer: [
    { id:'awareness', label:'Awareness', icon:'👁', color:'#7c6fcd' },
    { id:'evaluation', label:'Evaluation', icon:'🔍', color:'#0369a1' },
    { id:'purchase', label:'Purchase / Procurement', icon:'💰', color:'#166534' },
    { id:'onboarding', label:'Onboarding', icon:'🚀', color:'#0369a1' },
    { id:'renewal', label:'Renewal / Advocacy', icon:'🔄', color:'#065f46' },
  ],
  user: [
    { id:'discovery', label:'Discovery', icon:'🌟', color:'#7c6fcd' },
    { id:'first_use', label:'First Use', icon:'▶️', color:'#0369a1' },
    { id:'engagement', label:'Regular Engagement', icon:'🔥', color:'#166534' },
    { id:'mastery', label:'Mastery / Completion', icon:'🎯', color:'#0369a1' },
    { id:'advocacy', label:'Advocacy', icon:'📢', color:'#065f46' },
  ],
};

const ROLE_LABELS = { team:'Team', coach:'Coach', beneficiary:'Company', client:'Client' };

export default function CanvasCoachView({ companyId, profile }) {
  const [canvasVersions, setCanvasVersions] = useState([]);
  const [journeyVersions, setJourneyVersions] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [activeDoc, setActiveDoc]           = useState('canvas'); // 'canvas' | 'journey'
  const [expandedVersions, setExpandedVersions] = useState({});
  const [comments, setComments]             = useState({});
  const [commentDrafts, setCommentDrafts]   = useState({});
  const [journeyTrack, setJourneyTrack]     = useState('customer');

  useEffect(() => {
    if (!companyId) return;
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'companies', companyId, 'canvasVersions'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
        setCanvasVersions(all.filter(v => v.type === 'bmc'));
        setJourneyVersions(all.filter(v => v.type === 'cjm'));

        // Load canvas comments
        const ccSnap = await getDocs(collection(db, 'companies', companyId, 'canvasComments'));
        const jcSnap = await getDocs(collection(db, 'companies', companyId, 'journeyComments'));
        const commentMap = {};
        [...ccSnap.docs, ...jcSnap.docs].forEach(d => {
          const data = { id: d.id, ...d.data() };
          const key = data.blockId || data.key || `${data.track}_${data.stageId}`;
          if (!commentMap[key]) commentMap[key] = [];
          commentMap[key].push(data);
        });
        setComments(commentMap);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyId]);


  const postCanvasComment = async (blockId) => {
    const text = commentDrafts[blockId]?.trim();
    if (!text || !companyId) return;
    try {
      const commentData = {
        blockId, body: text,
        authorUid: profile.uid, authorName: profile.displayName, authorRole: profile.role,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'companies', companyId, 'canvasComments'), commentData);
      setComments(prev => ({ ...prev, [blockId]: [...(prev[blockId]||[]), { id: ref.id, ...commentData, createdAt: new Date() }] }));
      setCommentDrafts(prev => ({ ...prev, [blockId]: '' }));
    } catch (err) { console.error(err); }
  };

  const postJourneyComment = async (key) => {
    const text = commentDrafts[key]?.trim();
    if (!text || !companyId) return;
    const parts = key.split('_');
    const track = parts[0];
    const stageId = parts.slice(1).join('_');
    try {
      const commentData = {
        key, track, stageId, body: text,
        authorUid: profile.uid, authorName: profile.displayName, authorRole: profile.role,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'companies', companyId, 'journeyComments'), commentData);
      setComments(prev => ({ ...prev, [key]: [...(prev[key]||[]), { id: ref.id, ...commentData, createdAt: new Date() }] }));
      setCommentDrafts(prev => ({ ...prev, [key]: '' }));
    } catch (err) { console.error(err); }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  const versions = activeDoc === 'canvas' ? canvasVersions : journeyVersions;

  return (
    <div className="gm-section">
      {/* Tab selector */}
      <div className="gm-section-header">
        <h2 className="gm-section-title" style={{ marginBottom: 0 }}>Strategic documents</h2>
        <div className="gm-client-tabs">
          <button className={`gm-client-tab ${activeDoc === 'canvas' ? 'gm-client-tab--active' : ''}`}
            onClick={() => setActiveDoc('canvas')}>
            Business Model Canvas {canvasVersions.length > 0 && `(${canvasVersions.length})`}
          </button>
          <button className={`gm-client-tab ${activeDoc === 'journey' ? 'gm-client-tab--active' : ''}`}
            onClick={() => setActiveDoc('journey')}>
            Journey Map {journeyVersions.length > 0 && `(${journeyVersions.length})`}
          </button>
        </div>
      </div>

      {versions.length === 0 && (
        <div className="gm-empty-state">
          <p>No {activeDoc === 'canvas' ? 'Business Model Canvas' : 'Journey Map'} versions submitted yet.</p>
        </div>
      )}

      {/* Version list — collapsible */}
      {versions.map((version, idx) => {
        const isExpanded = expandedVersions[version.id];
        return (
          <div key={version.id} className="gm-session-card" style={{ marginBottom: 8 }}>
            {/* Version header */}
            <div className="gm-session-card-header"
              onClick={() => setExpandedVersions(prev => ({ ...prev, [version.id]: !prev[version.id] }))}>
              <div className="gm-session-card-left">
                <div className="gm-session-date">{formatDate(version.createdAt)}</div>
                <div className="gm-session-meta">
                  <span>by {version.savedByName}</span>
                  {idx === 0 && <span className="gm-validated-badge">Latest</span>}
                </div>
              </div>
              <span className="gm-session-toggle">{isExpanded ? '▲' : '▼'}</span>
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div className="gm-session-card-body">
                {activeDoc === 'canvas' && (
                  <CanvasVersionView
                    version={version}
                    comments={comments}
                    commentDrafts={commentDrafts}
                    setCommentDrafts={setCommentDrafts}
                    onPostComment={postCanvasComment}
                    profile={profile}
                  />
                )}
                {activeDoc === 'journey' && (
                  <JourneyVersionView
                    version={version}
                    comments={comments}
                    commentDrafts={commentDrafts}
                    setCommentDrafts={setCommentDrafts}
                    onPostComment={postJourneyComment}
                    profile={profile}
                    journeyTrack={journeyTrack}
                    setJourneyTrack={setJourneyTrack}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Canvas version view ──────────────────────────────────────
function CanvasVersionView({ version, comments, commentDrafts, setCommentDrafts, onPostComment, profile }) {
  const [activeComment, setActiveComment] = useState(null);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gridTemplateRows:'auto auto auto', gap:6, overflowX:'auto' }}>
        {CANVAS_GRID.map(({ blockId, gridColumn, gridRow }) => {
          const block = CANVAS_BLOCKS.find(b => b.id === blockId);
          const text  = version.blocks?.[blockId];
          const blockComments = comments[blockId] || [];
          const isOpen = activeComment === blockId;

          return (
            <div key={blockId} style={{ gridColumn, gridRow, background:'#fff', border:'1px solid #e2e8f0', borderTop:`3px solid ${block.color}`, borderRadius:8, padding:10, minHeight:100, position:'relative' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6, justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ fontSize:'0.65rem', fontWeight:800, color:block.color }}>{block.short}</span>
                  <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#475569' }}>{block.label}</span>
                </div>
                <button
                  className={`gm-bmc-comment-btn ${blockComments.length > 0 ? 'gm-bmc-comment-btn--has' : ''}`}
                  onClick={() => setActiveComment(isOpen ? null : blockId)}
                  title={`${blockComments.length} comment${blockComments.length !== 1 ? 's' : ''}`}
                >
                  💬{blockComments.length > 0 ? ` ${blockComments.length}` : ''}
                </button>
              </div>
              <div style={{ fontSize:'0.78rem', color: text ? '#1e293b' : '#cbd5e1', lineHeight:1.6, whiteSpace:'pre-wrap' }}>
                {text || 'Not filled in'}
              </div>

              {/* Sticky comment */}
              {isOpen && (
                <div className="gm-bmc-sticky">
                  <div className="gm-bmc-sticky-header">
                    <span>Comments — {block.label}</span>
                    <button className="gm-btn-icon" onClick={() => setActiveComment(null)}>✕</button>
                  </div>
                  <div className="gm-bmc-sticky-comments">
                    {blockComments.length === 0 && <p className="gm-comments-empty">No comments yet.</p>}
                    {blockComments.map((c, i) => (
                      <CommentBubble key={c.id||i} comment={c} isOwn={c.authorUid === profile?.uid} />
                    ))}
                  </div>
                  <div className="gm-comment-form">
                    <textarea placeholder="Add a comment…" rows={2}
                      value={commentDrafts[blockId] || ''}
                      onChange={e => setCommentDrafts(prev => ({ ...prev, [blockId]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onPostComment(blockId); }}}
                    />
                    <button className="gm-btn gm-btn-primary gm-btn-sm"
                      onClick={() => onPostComment(blockId)}
                      disabled={!commentDrafts[blockId]?.trim()}>Post</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Journey version view ─────────────────────────────────────
function JourneyVersionView({ version, comments, commentDrafts, setCommentDrafts, onPostComment, profile, journeyTrack, setJourneyTrack }) {
  const [activeComment, setActiveComment] = useState(null);
  const stages = JOURNEY_STAGE_DEFAULTS[journeyTrack] || [];

  return (
    <div>
      <div className="gm-journey-track-selector" style={{ marginBottom: 16 }}>
        <button className={`gm-journey-track-btn ${journeyTrack === 'customer' ? 'gm-journey-track-btn--active' : ''}`}
          onClick={() => setJourneyTrack('customer')}>💼 Customer Journey</button>
        <button className={`gm-journey-track-btn ${journeyTrack === 'user' ? 'gm-journey-track-btn--active' : ''}`}
          onClick={() => setJourneyTrack('user')}>🎓 User Journey</button>
      </div>

      <div className="gm-journey-grid">
        {stages.map(stage => {
          const stageData = version.data?.[journeyTrack]?.[stage.id] || {};
          const key = `${journeyTrack}_${stage.id}`;
          const stageComments = comments[key] || [];
          const isOpen = activeComment === key;
          const hasContent = Object.values(stageData).some(v => v);

          return (
            <div key={stage.id}
              className={`gm-journey-stage ${hasContent ? 'gm-journey-stage--filled' : ''}`}
              style={{ borderTop: `3px solid ${stage.color}` }}>
              <div className="gm-journey-stage-header">
                <span className="gm-journey-stage-icon">{stage.icon}</span>
                <div className="gm-journey-stage-title">{stage.label}</div>
                <button
                  className={`gm-bmc-comment-btn ${stageComments.length > 0 ? 'gm-bmc-comment-btn--has' : ''}`}
                  onClick={() => setActiveComment(isOpen ? null : key)}
                >
                  💬{stageComments.length > 0 ? ` ${stageComments.length}` : ''}
                </button>
              </div>

              <div className="gm-journey-stage-fields">
                {Object.entries(stageData).filter(([,v]) => v).map(([fieldId, value]) => (
                  <div key={fieldId} className="gm-journey-field">
                    <label className="gm-journey-field-label">{fieldId.replace(/_/g, ' ')}</label>
                    <div style={{ fontSize:'0.78rem', color:'#1e293b', lineHeight:1.5, whiteSpace:'pre-wrap', padding:'6px 0' }}>
                      {value}
                    </div>
                  </div>
                ))}
                {!hasContent && <p style={{ fontSize:'0.75rem', color:'#cbd5e1', fontStyle:'italic' }}>Not filled in</p>}
              </div>

              {/* Sticky comment */}
              {isOpen && (
                <div className="gm-bmc-sticky">
                  <div className="gm-bmc-sticky-header">
                    <span>Comments — {stage.label}</span>
                    <button className="gm-btn-icon" onClick={() => setActiveComment(null)}>✕</button>
                  </div>
                  <div className="gm-bmc-sticky-comments">
                    {stageComments.length === 0 && <p className="gm-comments-empty">No comments yet.</p>}
                    {stageComments.map((c, i) => (
                      <CommentBubble key={c.id||i} comment={c} isOwn={c.authorUid === profile?.uid} />
                    ))}
                  </div>
                  <div className="gm-comment-form">
                    <textarea placeholder="Add a comment…" rows={2}
                      value={commentDrafts[key] || ''}
                      onChange={e => setCommentDrafts(prev => ({ ...prev, [key]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onPostComment(key); }}}
                    />
                    <button className="gm-btn gm-btn-primary gm-btn-sm"
                      onClick={() => onPostComment(key)}
                      disabled={!commentDrafts[key]?.trim()}>Post</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Comment bubble ───────────────────────────────────────────
function CommentBubble({ comment, isOwn }) {
  const date = comment.createdAt?.toDate ? comment.createdAt.toDate() : comment.createdAt instanceof Date ? comment.createdAt : null;
  return (
    <div className={`gm-comment ${isOwn ? 'gm-comment--own' : ''}`}>
      <div className="gm-comment-meta">
        <span className="gm-comment-author">{comment.authorName}</span>
        <span className={`gm-comment-role gm-action-assignee--${comment.authorRole}`}>
          {ROLE_LABELS[comment.authorRole] || comment.authorRole}
        </span>
        {date && <span className="gm-comment-time">{date.toLocaleDateString('en-GB', { day:'numeric', month:'short' })}</span>}
      </div>
      <div className="gm-comment-body">{comment.body}</div>
    </div>
  );
}
